import {
  Component, inject, input, output, signal, OnInit,
  ElementRef, viewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { PaymentService } from '../../../../core/services/payment.service';
import { loadScript } from '../../../../core/utils/script-loader';

declare const paypal: any;

@Component({
  selector: 'app-paypal-gateway',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule, TranslateModule],
  template: `
    <div class="paypal-gateway">
      @if (loading()) {
        <div class="gateway-loading">
          <mat-spinner diameter="36"></mat-spinner>
          <p>{{ 'PAYMENT_PROCESSING.PAYPAL.LOADING' | translate }}</p>
        </div>
      }

      <div #paypalContainer class="paypal-button-container"></div>

      @if (!loading() && !processing()) {
        <p class="paypal-hint">{{ 'PAYMENT_PROCESSING.PAYPAL.PAY_HINT' | translate }}</p>
      }

      @if (processing()) {
        <div class="gateway-loading">
          <mat-spinner diameter="36"></mat-spinner>
          <p>{{ 'PAYMENT_PROCESSING.BRAINTREE.PROCESSING' | translate }}</p>
        </div>
      }
    </div>
  `,
  styles: [`
    .paypal-gateway {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 16px;
    }
    .gateway-loading {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      padding: 24px;
      color: #666;
    }
    .paypal-button-container {
      width: 100%;
      max-width: 320px;
      min-height: 48px;
    }
    .paypal-hint {
      margin: 8px 0 0;
      text-align: center;
      font-size: 13px;
      color: #666;
    }
  `]
})
export class PaypalGatewayComponent implements OnInit {
  orderId = input.required<number>();
  gatewayData = input<Record<string, any>>();
  // ISO currency code of the order (e.g. 'EUR', 'BRL'). The SDK currency
  // must match the server-side order currency, so this is threaded down
  // from the order rather than hardcoded. Defaults to EUR as a safe floor.
  currency = input<string>('EUR');

  completed = output<{ orderId: number; status: string }>();
  // Carries the HTTP status when known so the host can react to it — e.g. a
  // 400 (wrong state, often already-paid) should trigger an order reload.
  failed = output<{ message: string; status?: number }>();

  private paymentService = inject(PaymentService);
  private translate = inject(TranslateService);
  private paypalContainer = viewChild<ElementRef>('paypalContainer');

  loading = signal(true);
  processing = signal(false);

  async ngOnInit(): Promise<void> {
    try {
      const config = await firstValueFrom(this.paymentService.getPaypalConfig(this.orderId()));
      const currency = encodeURIComponent(this.currency());
      await loadScript(
        `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(config.clientId)}&currency=${currency}&intent=capture`,
        'paypal-sdk'
      );

      this.loading.set(false);

      const container = this.paypalContainer()?.nativeElement;
      if (!container) {
        this.failed.emit({ message: this.translate.instant('PAYMENT_PROCESSING.ERROR_DESC') });
        return;
      }

      paypal.Buttons({
        // Render only the PayPal button — no Debit/Credit Card funding option.
        fundingSource: paypal.FUNDING.PAYPAL,
        createOrder: async () => {
          const result = await firstValueFrom(this.paymentService.createPaypalOrder(this.orderId()));
          return result.orderId;
        },
        onApprove: async (data: any) => {
          this.processing.set(true);
          try {
            const result = await firstValueFrom(
              this.paymentService.executePaypal(this.orderId(), data.orderID)
            );
            // A non-success capture also arrives as HTTP 422 (→ catch). Per the
            // confirmed flow, do nothing on failure: reset so the buyer can
            // retry the PayPal button.
            if (!result.success) {
              this.processing.set(false);
              return;
            }
            // Capture accepted. /execute only reports success (covers both a
            // confirmed order and a pending one), so don't claim 'approved'
            // here — emit a neutral pending status. The dialog refreshes the
            // order-details view and closes; that refreshed view reads the real
            // order status (confirmed vs pending) and is the confirmation surface.
            this.completed.emit({ orderId: this.orderId(), status: 'payment_pending' });
          } catch (err: any) {
            console.error('[PaypalGateway] Execute error:', err);
            this.processing.set(false);
            // A 400 here means the order is no longer in a payable state —
            // typically because it was already captured. Surface it so the
            // host can reload and confirm. 422 (declined / amount or currency
            // mismatch) → do nothing per the confirmed flow; leave the button.
            const status = Number(err?.status) || undefined;
            if (status === 400) {
              this.failed.emit({ message: this.translate.instant('PAYMENT_PROCESSING.ERROR_DESC'), status });
            }
          }
        },
        onError: (err: any) => {
          console.error('[PaypalGateway] Button error:', err);
          this.failed.emit({ message: this.translate.instant('PAYMENT_PROCESSING.ERROR_DESC') });
        }
      }).render(container);
    } catch (err: any) {
      console.error('[PaypalGateway] Init error:', err);
      this.loading.set(false);
      // Re-attempts (e.g. after "Tentar novamente") can 400 here when the
      // order is already paid — pass the status so the host reloads & confirms.
      this.failed.emit({
        message: this.translate.instant('PAYMENT_PROCESSING.ERROR_DESC'),
        status: Number(err?.status) || undefined
      });
    }
  }
}
